<template>
    <TooltipCustom
        :inline
        :small
        :trigger-title="isDev ? path : undefined"
        :page-type="pageType"
        @toggle="isClosed = !$event"
    >
        <template
            v-if="$slots['trigger']"
            #trigger
        >
            <slot name="trigger" />
        </template>

        <template
            v-if="$slots['label']"
            #label
        >
            <slot name="label" />
        </template>

        <template #title>
            <template v-if="fetching">
                {{ t('globals.loading') }}
            </template>

            <template v-else-if="data && data.tooltip">
                {{ data.tooltip.title }}
            </template>

            <template v-else-if="!errors.length">Not found</template>

            <template v-else>
                {{ t('globals.critical_error_title') }}
            </template>
        </template>

        <template #content>
            <GridContentLoader v-if="fetching" />

            <ContentComponentItems
                v-else-if="data && data.tooltip"
                :data="data.tooltip.content"
                :page-type="pageType"
            />

            <div v-else-if="!errors.length">Tooltip not found</div>

            <ErrorFiveHundredMessage
                v-else
                :errors="errors"
            ></ErrorFiveHundredMessage>
        </template>
    </TooltipCustom>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useTranslator } from '@/i18n'
import { useResponseError } from '@/helpers/errors'
import { useTooltipQuery } from '@/graphql/api/cms/tooltip.query.generated.ts'

import GridContentLoader from '@/components/ui/GridContentLoader.vue'
import ErrorFiveHundredMessage from '@/components/ui/error/ErrorFiveHundredMessage.vue'
import TooltipCustom from '@/components/layout/tooltip/TooltipCustom.vue'
import ContentComponentItems from '@/components/cms/component/ContentComponentItems.vue'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'

const { path } = defineProps<{
    path: string
    small?: boolean
    inline?: boolean
    pageType: PageType
}>()

const { locale } = useTranslator()
const { t } = useI18n()

const isClosed = ref(true)

const response = useTooltipQuery({
    variables: computed(() => ({
        path,
        locale: locale.value,
    })),
    pause: () => isClosed.value,
})

const { data, fetching } = response

const errors = useResponseError(response)

const isDev = import.meta.env.DEV
</script>
